import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { fetchTeamDetails } from '../../store/actions/organization/teamActions';
import {
  connectTeamToResource,
  disconnectTeamFromResource,
  fetchTeamsListLightweight,
} from '../../store/actions/collaboratorActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import Dropdown from './TailwindComponents/Dropdown';
import Button from './TailwindComponents/Button';
import Badge from './TailwindComponents/Badge';
import ProfileImage from '../Header/ProfileImage';
import { PiUsersThreeLight, PiTrashLight, PiSpinnerLight, PiCaretDownLight } from 'react-icons/pi';
import { normalise } from './access/accessModel';
import Spinner from './TailwindComponents/Spinner';

interface ConnectedTeam {
  team_uuid: string;
  team_name: string;
  member_count: number;
}

interface TeamMemberPreview {
  user_uuid: string;
  name: string;
  email: string;
  initials: string;
  profile_picture: string | null;
  access: string;
}

interface TeamConnectorSectionProps {
  resourceUuid: string;
  resourceType: string;
  connectedTeams: ConnectedTeam[];
  onTeamConnected: () => void;
  onTeamDisconnected: () => void;
  canAdmin?: boolean;
  existingCollaboratorUuids?: string[];
}

const ACCESS_OPTIONS = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
  { value: 'admin', label: 'Admin' },
];

/**
 * Team selector - renders the dropdown to connect a new team.
 * Place this in the top section of the side panel (alongside the People selector).
 */
const TeamConnectorSection: React.FC<TeamConnectorSectionProps> = ({
  resourceUuid,
  resourceType,
  connectedTeams,
  onTeamConnected,
  onTeamDisconnected,
  canAdmin = true,
  existingCollaboratorUuids = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [teams, setTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [memberPreviews, setMemberPreviews] = useState<TeamMemberPreview[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    setLoadingTeams(true);
    (dispatch as any)(fetchTeamsListLightweight())
      .then((result: any) => setTeams(result || []))
      .finally(() => setLoadingTeams(false));
  }, [dispatch]);

  const connectedTeamIds = useMemo(
    () => new Set(connectedTeams.map((ct) => ct.team_uuid)),
    [connectedTeams]
  );

  const teamOptions = useMemo(
    () =>
      teams
        .filter((team) => !connectedTeamIds.has(team.id || team.uuid))
        .map((team) => ({ value: team.id || team.uuid, label: team.name })),
    [teams, connectedTeamIds]
  );

  useEffect(() => {
    if (!selectedTeamId) { setMemberPreviews([]); return; }
    setLoadingMembers(true);
    (dispatch as any)(fetchTeamDetails(selectedTeamId))
      .then((team: any) => {
        if (team?.members) {
          const existingSet = new Set(existingCollaboratorUuids);
          setMemberPreviews(
            team.members
              .filter((m: any) => !existingSet.has(m.id || m.user_id || m.uuid))
              .map((m: any) => ({
                user_uuid: m.id || m.user_id || m.uuid,
                name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
                email: m.email || '',
                initials: m.initials || `${(m.first_name || '')[0] || ''}${(m.last_name || '')[0] || ''}`.toUpperCase(),
                profile_picture: m.profile_picture || null,
                access: 'edit',
              }))
          );
        }
      })
      .finally(() => setLoadingMembers(false));
  }, [dispatch, selectedTeamId, existingCollaboratorUuids]);

  const handleMemberAccessChange = (userUuid: string, newAccess: string) => {
    setMemberPreviews((prev) => prev.map((m) => (m.user_uuid === userUuid ? { ...m, access: newAccess } : m)));
  };

  const handleConnect = async () => {
    if (!selectedTeamId) return;
    setConnecting(true);
    try {
      const overrides = memberPreviews.map((m) => ({ user_uuid: m.user_uuid, access: m.access }));
      const result = await (dispatch as any)(connectTeamToResource(resourceType, resourceUuid, selectedTeamId, overrides));
      if (result) { setSelectedTeamId(null); setMemberPreviews([]); onTeamConnected(); }
    } finally { setConnecting(false); }
  };

  if (!canAdmin) return null;

  return (
    <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-3">
        <PiUsersThreeLight className="w-4 h-4 text-primary-600" />
        <p className="text-xs font-medium text-fg-muted">
          {t('Teams')}
        </p>
      </div>

      {loadingTeams ? (
        <div className="flex items-center gap-2 py-3 justify-center">
          <Spinner size="glyph" className="!h-5 !w-5" />
          <span className="text-xs text-gray-400">{t('Loading teams')}...</span>
        </div>
      ) : (
        <Dropdown
          options={teamOptions}
          value={selectedTeamId || ''}
          onChange={(val) => setSelectedTeamId((Array.isArray(val) ? val[0] : val) || null)}
          placeholder={teamOptions.length === 0 ? t('No teams available') : t('Select a team to connect')}
          width="w-full"
        />
      )}

      {selectedTeamId && (
        <div className="mt-3">
          {loadingMembers ? (
            <div className="flex items-center gap-2 py-3 justify-center">
              <Spinner size="glyph" className="!h-5 !w-5" />
              <span className="text-xs text-gray-400">{t('Loading members')}...</span>
            </div>
          ) : memberPreviews.length > 0 ? (
            <>
              <div className="glass space-y-1.5 p-3 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('New members to add ({{count}})', { count: memberPreviews.length })}
                </p>
                {memberPreviews.map((member) => (
                  <div key={member.user_uuid} className="flex items-center justify-between gap-2 py-1.5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <ProfileImage profilePicture={normalise(member).avatar} email={member.email} initials={member.initials} name={member.name} size="w-6 h-6" />
                      <span className="text-xs text-gray-900 dark:text-gray-100 truncate">{member.name || member.email}</span>
                    </div>
                    <Dropdown
                      options={ACCESS_OPTIONS}
                      value={member.access}
                      onChange={(val) => handleMemberAccessChange(member.user_uuid, Array.isArray(val) ? val[0] : val)}
                      compact
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="primary" size="small" onClick={handleConnect} disabled={connecting || memberPreviews.length === 0}>
                  {connecting ? t('Adding...') : t('Add')}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400 py-2 text-center">{t('All team members already have access')}</p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Connected teams display - renders below the collaborator list.
 * Shows connected teams with expandable member lists and access controls.
 */
export const ConnectedTeamsList: React.FC<{
  resourceUuid: string;
  resourceType: string;
  connectedTeams: ConnectedTeam[];
  onTeamDisconnected: () => void;
  canAdmin?: boolean;
}> = ({ resourceUuid, resourceType, connectedTeams, onTeamDisconnected, canAdmin = true }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [teamMembers, setTeamMembers] = useState<Record<string, TeamMemberPreview[]>>({});
  const [loadingTeamMembers, setLoadingTeamMembers] = useState<Set<string>>(new Set());
  const [disconnecting, setDisconnecting] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((teamUuid: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamUuid)) {
        next.delete(teamUuid);
      } else {
        next.add(teamUuid);
        // Fetch members if not already loaded
        if (!teamMembers[teamUuid]) {
          setLoadingTeamMembers((prev) => new Set(prev).add(teamUuid));
          (dispatch as any)(fetchTeamDetails(teamUuid))
            .then((team: any) => {
              if (team?.members) {
                setTeamMembers((prev) => ({
                  ...prev,
                  [teamUuid]: team.members.map((m: any) => ({
                    user_uuid: m.id || m.user_id || m.uuid,
                    name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
                    email: m.email || '',
                    initials: m.initials || `${(m.first_name || '')[0] || ''}${(m.last_name || '')[0] || ''}`.toUpperCase(),
                    profile_picture: m.profile_picture || null,
                    access: 'edit',
                  })),
                }));
              }
            })
            .finally(() => {
              setLoadingTeamMembers((prev) => { const n = new Set(prev); n.delete(teamUuid); return n; });
            });
        }
      }
      return next;
    });
  }, [dispatch, teamMembers]);

  const handleDisconnect = (teamUuid: string, teamName: string) => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Disconnect team'),
      subMsg: t('Disconnect "{{name}}"? All team members will lose access granted through this team.', { name: teamName }),
      isDest: true,
      onConfirm: async () => {
        dispatch(hideModal());
        setDisconnecting((prev) => new Set(prev).add(teamUuid));
        try {
          const result = await (dispatch as any)(disconnectTeamFromResource(resourceType, resourceUuid, teamUuid));
          if (result) onTeamDisconnected();
        } finally {
          setDisconnecting((prev) => { const n = new Set(prev); n.delete(teamUuid); return n; });
        }
      },
    }));
  };

  if (connectedTeams.length === 0) return null;

  return (
    <div className="px-5 py-3">
      <p className="text-xs font-medium text-fg-muted mb-2">
        {t('Connected teams')}
      </p>
      <div className="space-y-1">
        {connectedTeams.map((ct) => {
          const isExpanded = expandedTeams.has(ct.team_uuid);
          const members = teamMembers[ct.team_uuid];
          const isLoadingMembers = loadingTeamMembers.has(ct.team_uuid);

          return (
            <div key={ct.team_uuid} className="rounded-lg border border-gray-100 dark:border-gray-800 overflow-hidden">
              {/* Team header row */}
              <div className="flex items-center gap-2 py-2 px-3 bg-gray-50/50 dark:bg-gray-800/30">
                <button
                  onClick={() => toggleExpand(ct.team_uuid)}
                  className="flex items-center gap-2 min-w-0 flex-1 text-start"
                >
                  <PiCaretDownLight className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                  <PiUsersThreeLight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 dark:text-white truncate">{ct.team_name}</span>
                  <Badge color="gray">{ct.member_count}</Badge>
                </button>
                {canAdmin && (
                  <button
                    onClick={() => handleDisconnect(ct.team_uuid, ct.team_name)}
                    disabled={disconnecting.has(ct.team_uuid)}
                    className="p-1 rounded text-gray-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors disabled:opacity-50 flex-shrink-0"
                    title={t('Disconnect team')}
                  >
                    <PiTrashLight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Expanded member list */}
              {isExpanded && (
                <div className="px-3 py-2 space-y-1 border-t border-gray-100 dark:border-gray-800">
                  {isLoadingMembers ? (
                    <div className="flex items-center gap-2 py-2 justify-center">
                      <Spinner size="glyph" className="!h-4 !w-4" />
                      <span className="text-xs text-gray-400">{t('Loading')}...</span>
                    </div>
                  ) : members ? (
                    members.map((member) => (
                      <div key={member.user_uuid} className="flex items-center justify-between gap-2 py-1">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <ProfileImage profilePicture={normalise(member).avatar} email={member.email} initials={member.initials} name={member.name} size="w-6 h-6" />
                          <div className="min-w-0">
                            <p className="text-xs text-gray-900 dark:text-gray-100 truncate">{member.name || member.email}</p>
                            <p className="text-[10px] text-gray-400 truncate">{member.email}</p>
                          </div>
                        </div>
                        <Badge color="blue">{t('via team')}</Badge>
                      </div>
                    ))
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamConnectorSection;
